import React, { forwardRef, useEffect, useState } from 'react'

import { useField } from 'formik'
import { useTranslation } from 'react-i18next'
import ReactSelect, { ActionMeta, GroupBase, components as reactSelectComponents, Props as SelectProps } from 'react-select'
import { MultiValue } from 'react-select/dist/declarations/src/types'
import styled from 'styled-components'
import { SpaceProps, WidthProps } from 'styled-system'

import { Checkbox, Select } from '@/atoms/input'
import { Box } from '@/atoms/layout'
import { SelectOption } from '@/models/common'

import FieldWrapper from './field-wrapper'

interface OwnProps {
  label?: string
  fieldProps?: SpaceProps & WidthProps
  placeholder?: string
  name?: string
  withSelectAll?: boolean
  hideErrorMessage?: boolean
  onSelectChange?: (values: Array<SelectOption>) => void
  tooltip?: string
}

export type MultiSelectFieldProps = OwnProps & SelectProps<SelectOption, boolean, GroupBase<SelectOption>>

const Option = (props): React.JSX.Element => {
  const OptionComponent = reactSelectComponents.Option as React.ComponentType<typeof props>
  return (
    <OptionComponent {...props}>
      <Checkbox onClick={() => null} selected={props.isSelected} label={props.label} isOption />
    </OptionComponent>
  )
}

const MenuList = (props): React.JSX.Element => {
  const MenuListComponent = reactSelectComponents.MenuList as React.ComponentType<typeof props>
  return (
    <MenuListComponent {...props}>
      {props.selectProps.withSelectAll && (
        <SelectAllWrapper>
          <Checkbox onClick={props.selectProps.selectAll} selected={props.selectProps.selectedAll} label={'Select All'} isOption />
        </SelectAllWrapper>
      )}
      {props.children}
    </MenuListComponent>
  )
}

const MultiSelectField = forwardRef<React.ElementRef<typeof ReactSelect>, MultiSelectFieldProps>(
  (
    {
      label,
      options,
      fieldProps,
      placeholder,
      name,
      components,
      withSelectAll,
      hideErrorMessage,
      isLoading,
      onSelectChange,
      tooltip,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [field, meta, helpers] = useField(name)
    const [selectedAll, setSelectedAll] = useState(false)
    const [menuIsOpen, setMenuIsOpen] = useState(false) // new state for managing menu open/close
    const hasError: boolean = meta.touched && !!meta.error
    const selectedOptions = options?.filter((option: SelectOption) => field.value?.includes(option.value)) ?? []

    // useEffect to watch for changes in field.value
    useEffect(() => {
      if (field.value.length === 0) {
        setSelectedAll(false)
      }
    }, [field.value])

    const handleOnChange = (values: Array<SelectOption>, actionMeta?: ActionMeta<SelectOption>): void => {
      const { action } = actionMeta
      if (action === 'clear') {
        selectedAll && setSelectedAll(false)
        onSelectChange && onSelectChange([])
        helpers.setValue([])
        return
      }
      if ((action === 'deselect-option' || action === 'remove-value' || action === 'pop-value') && selectedAll) {
        selectedAll && setSelectedAll(false)
      }

      const selectedValues = values?.map(option => option.value) ?? []

      onSelectChange && onSelectChange(selectedValues as Array<SelectOption>)

      helpers.setValue(selectedValues)
    }
    const selectAll = (selected: boolean) => {
      setSelectedAll(!selected)
      if (!selected) {
        setSelectedAll(true)
        setMenuIsOpen(false) // close the menu when select all
        return helpers?.setValue(options.map((option: SelectOption) => option.value))
      }

      return helpers?.setValue([])
    }

    return (
      <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} hideErrorMessage={hideErrorMessage} tooltip={tooltip}>
        <Select
          ref={ref}
          options={options}
          hasError={hasError}
          value={selectedOptions as MultiValue<SelectOption>}
          name={field.name}
          onChange={handleOnChange}
          isMulti
          components={{ Option, MenuList, ...components }}
          closeMenuOnSelect={selectedAll}
          hideSelectedOptions={false}
          menuIsOpen={menuIsOpen} // manage menu open/close state
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => setMenuIsOpen(false)}
          placeholder={isLoading ? `${t('global.label.loading')}...` : placeholder || t('global.placeholder.select')}
          withSelectAll={withSelectAll}
          selectAll={selectAll}
          selectedAll={selectedAll}
          isLoading={isLoading}
          {...rest}
        />
      </FieldWrapper>
    )
  },
)

MultiSelectField.displayName = 'MultiSelectField'

export default MultiSelectField

const SelectAllWrapper = styled(Box)`
  padding: ${({ theme }) => `${theme.space.S12} ${theme.space.S16}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgLight};
  }
`
